<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="tw-price-details">
    <table class="tw-price-details__table">
      <tbody>
        <tr v-if="packageTypeData.hasTrial">
          <th>
            {{ translate(`generate.trial.periods.${periodsData.trialPeriodType}`, locale, {
              count: periodsData.trialPeriod
            }) }}
          </th>
          <td>
            {{ pricesData.trialPrice }}
          </td>
        </tr>
        <tr>
          <th>
            <div>
              <span v-if="packageTypeData.hasTrial">{{
                translate(`generate.periods.thenEveryPeriods`, locale, {
                  period: translate(`generate.periods.withoutSingleCount.${periodsData.periodType}`, locale, { count: periodsData.period })
                })
              }}
              </span>
              <span v-else>{{ translate(`generate.subscription.periods.${periodsData.periodType}`, locale, { count: periodsData.period }) }}</span>
            </div>
            <div class="tw-price-details__table--secondary-text text-[10px] leading-4">
              {{ translate(`generate.periods.startingOn`, locale, { date: startingOnDate }) }}
            </div>
          </th>
          <td>
            {{ pricesData.price }}
          </td>
        </tr>
      </tbody>
    </table>
    <div class="text-[8px] leading-none lg:text-[10px]">
      <span
        v-html="purify(translate('generate.payment.priceDetails.renewalInfo', locale, {
          cancelationLink: `<a href='//account.zotlo.com' target='_blank' class='!no-underline hover:!underline !text-[inherit]'>account.zotlo.com</a>`
        }))"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { type PropType, computed } from 'vue';
import type { PageOptions } from '@zotlo-shared/types/model';
import type { ElementOptions } from '@zotlo-shared/types/options';
import { hexToRgba } from '@zotlo-shared/utils/helpers';
import { useTranslate } from '@zotlo-shared/composable/useTranslate';
import { getPackagePrices, getPackagePeriodsInfo, getPackageTypeConditions, addPeriod } from '@zotlo-shared/elements/common/payment/utils';
import { purify } from '@zotlo-shared/utils';

const props = defineProps({
  isEditorMode: { type: Boolean, default: true },
  config: { type: Object as PropType<ElementOptions['payment']>, default: () => ({}) },
  pageData: { type: Object, default: () => ({}) },
  pageOptions: { type: Object as PropType<PageOptions>, default: () => ({}) },
  locale: { type: String, default: '' }
});

const { translate } = useTranslate();

const options = computed(() => props.config?.priceDetails || {});
const isTrialUsed = computed(() => props.pageData.value?.isTrialUsed || false);
const pricesData = computed<Record<string, any>>(() => getPackagePrices(props.pageData?.selectedPackage, props.locale));
const periodsData = computed(() => getPackagePeriodsInfo(props.pageData?.selectedPackage, { ignoredDaysToWeek: [7] }));
const packageTypeData = computed(() => getPackageTypeConditions(props.pageData?.selectedPackage, { isTrialUsed: isTrialUsed.value }));

const paymentStartDate = computed(() => {
  const { trialPeriod, trialPeriodType } = periodsData.value;
  const today = new Date();

  return packageTypeData.value.hasTrial
    ? addPeriod(today, trialPeriod, trialPeriodType)
    : today;
});

const startingOnDate = computed(() =>
  paymentStartDate.value.toLocaleDateString(props.locale, { month: 'short', day: 'numeric' })
);

const priceDetailsStyle = computed(() => {
  const o = options.value;
  const backgroundColor = o?.background?.color || "#F6F7F9";
  const borderColor = o?.border?.color || "#E7EAEE";
  const textColor = o?.text?.color?.primary || "#0D0626";
  const secondaryTextColor = o?.text?.color?.secondary || "#818A9C";

  return {
    backgroundColor: hexToRgba(backgroundColor, o?.background?.opacity),
    borderColor,
    borderRadius: `${o?.border?.radius ?? 8}px`,
    borderWidth: `${o?.border?.width ?? 1}px`,
    textColor,
    secondaryTextColor,
    fontFamily: props.pageOptions?.fontFamily
  }
})
</script>

<style lang="postcss" scoped>
.tw-price-details {
  --borderColor: v-bind(priceDetailsStyle.borderColor);
  --secondaryTextColor: v-bind(priceDetailsStyle.secondaryTextColor);
  --textColor: v-bind(priceDetailsStyle.textColor);
  --fontFamily: v-bind(priceDetailsStyle.fontFamily);
  --bgColor: v-bind(priceDetailsStyle.backgroundColor);
  --borderWidth: v-bind(priceDetailsStyle.borderWidth);
  --borderRadius: v-bind(priceDetailsStyle.borderRadius);

  background-color: var(--bgColor);
  border-width: var(--borderWidth);
  border-style: solid;
  border-color: var(--borderColor);
  border-radius: var(--borderRadius);
  font-family: var(--fontFamily);
  color: var(--textColor);

  @apply px-[12px] py-[8px] flex flex-col gap-[6px];

  &__table {
    @apply w-full ;

    td, th {
      @apply py-[6px] border-b;
      border-color: var(--borderColor);
    }

    th {
      @apply text-left text-[12px] leading-none font-medium;
    }

    td {
      @apply text-right text-[14px] leading-none font-semibold;
    }

    &--secondary-text {
      color: var(--secondaryTextColor);
    }
  }
}

[dir="rtl"] .tw-price-details {
  &__table {
    th { @apply text-right; }
    td { @apply text-left; }
  }
}
</style>
